/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useCallback, useRef, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { SplitLayout } from '../split-layout/SplitLayout';
import {
	type SessionContextPresentation,
	SplitViewContext,
} from '../split-layout/SplitViewContext';

import { SessionContextPanelDrawer } from './SessionContextPanelDrawer';

const styles = cssMap({
	overlayHost: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minHeight: '0',
		borderRadius: 'inherit',
	},
});

export type SessionContextLayoutProps = {
	children: ReactNode;
	isMiniModal: boolean;
};

/**
 * Owns the enabled session-context experience. Callers must only mount this component after both
 * `isContextPanelEnabled` and `agent_session_experience_fg` have passed.
 */
export const SessionContextLayout = ({
	children,
	isMiniModal,
}: SessionContextLayoutProps): JSX.Element => {
	const { sendAnalyticsEvent } = useAnalytics();
	const [presentation, setPresentation] = useState<SessionContextPresentation | null>(null);
	const rightPanelExitHandlerRef = useRef<(() => void) | null>(null);
	const closeSessionContext = useCallback(() => setPresentation(null), []);
	const openSessionContext = useCallback(
		(requestedPresentation: SessionContextPresentation): SessionContextPresentation => {
			const nextPresentation = isMiniModal ? 'drawer' : requestedPresentation;
			setPresentation(nextPresentation);

			sendAnalyticsEvent({
				action: 'viewed',
				actionSubject: 'sessionContextPanel',
				attributes: {
					presentation: nextPresentation,
				},
			});

			return nextPresentation;
		},
		[isMiniModal, sendAnalyticsEvent],
	);

	return (
		<SplitViewContext.Provider
			value={{
				presentation,
				openSessionContext,
				closeSessionContext,
				rightPanelExitHandlerRef,
			}}
		>
			<div css={styles.overlayHost}>
				<SplitLayout
					isRightPanelVisible={presentation === 'split'}
					onAutoCollapse={closeSessionContext}
					rightPanelExitHandlerRef={rightPanelExitHandlerRef}
				>
					{children}
				</SplitLayout>
				{/* Kept mounted (not gated by `presentation`) so it can play its slide-out exit animation
				    on close, mirroring the hamburger side-nav. */}
				<SessionContextPanelDrawer isOpen={presentation === 'drawer'} isMiniModal={isMiniModal} />
			</div>
		</SplitViewContext.Provider>
	);
};
